/**
 * Download/salvar arquivo cross-plataforma.
 *
 * O atalho clássico `<a download>` NÃO funciona no iOS: Safari e PWA
 * standalone ignoram o atributo (pior ainda com data:/blob:) — era o
 * "documentos não baixam no celular" relatado pela myCar em 08/08/2026.
 *
 * Estratégia, na ordem:
 *   1. Mobile com Web Share API de ARQUIVOS → share sheet nativo
 *      ("Salvar em Arquivos" / WhatsApp / e-mail no iPhone e Android).
 *   2. iOS sem share de arquivos → abre o blob numa aba: o visualizador
 *      nativo mostra o PDF/imagem e o usuário salva/compartilha de lá.
 *   3. Desktop (e Android sem share) → <a download> tradicional.
 */

export const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const isMobileLike = () =>
  isIOS() || /Android/i.test(navigator.userAgent);

export function base64ToBlob(base64, mimeType = 'application/octet-stream') {
  const byteString = atob(base64);
  const bytes = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i += 1) {
    bytes[i] = byteString.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
}

const anchorDownload = (href, fileName) => {
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName || `arquivo_${Date.now()}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Salva/compartilha um Blob do jeito que a plataforma suporta.
 * Retorna true quando alguma via foi acionada (inclusive share cancelado
 * pelo usuário — a intenção chegou até ele).
 */
export async function saveBlobCrossPlatform(blob, fileName) {
  const name = fileName || `arquivo_${Date.now()}`;

  // 1. Share sheet nativo com arquivo (mobile). Precisa rodar dentro do
  // gesto do usuário — os chamadores são onClick, então ok.
  if (isMobileLike() && typeof navigator.share === 'function' && navigator.canShare) {
    try {
      const file = new File([blob], name, { type: blob.type || 'application/octet-stream' });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file] });
        return true;
      }
    } catch (err) {
      // AbortError = usuário fechou o share sheet — não insistir por outra via.
      if (err && err.name === 'AbortError') return true;
      // Qualquer outro erro: cai para as próximas vias.
    }
  }

  const objectUrl = URL.createObjectURL(blob);
  try {
    if (isIOS()) {
      // 2. iOS ignora o atributo download: abre no visualizador nativo.
      const win = window.open(objectUrl, '_blank');
      if (!win) anchorDownload(objectUrl, name); // popup bloqueado: melhor esforço
      return true;
    }
    // 3. Caminho clássico.
    anchorDownload(objectUrl, name);
    return true;
  } finally {
    // Delay: o navegador precisa do objectURL vivo até consumir o clique/aba.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  }
}

/** Salva um payload base64 (retorno dos endpoints de download de mídia). */
export async function saveBase64File(base64, mimeType, fileName) {
  const blob = base64ToBlob(base64, mimeType || 'application/octet-stream');
  return saveBlobCrossPlatform(blob, fileName);
}

/**
 * Salva um arquivo remoto por URL: baixa para blob (mantém o nome) e usa a
 * mesma via cross-plataforma; se o fetch falhar (CORS etc.), abre a URL.
 */
export async function saveUrlFile(url, fileName) {
  try {
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    return saveBlobCrossPlatform(blob, fileName);
  } catch (err) {
    window.open(url, '_blank', 'noopener');
    return true;
  }
}
